<template>
  <div class="relative">
    <!-- Actual component -->
    <cms-section
      disable-lazy-hydrate

      :first-section="firstElement"
      page-context
      :section="{ name: flagKey, items: cmsVariantToLoad }"
    />
    <div v-if="isSkeleton" class="absolute-0 z-1 bg-white">
      <div class="absolute-0 skeleton" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Experiment } from '#types/components/cms/experiment'
import type { OptimizelyDecision } from '@optimizely/optimizely-sdk'
import mappings from '#content/mappings'
import { enums } from '@optimizely/optimizely-sdk'

const { content } = defineProps<{ content: Experiment, firstElement?: boolean }>()

const { enableOptimizely } = useFeatureFlags().configP13n
const { decide } = useOptimizely()
const { $gtm, $optimizelyClient } = useNuxtApp()
const route = useRoute()
const mapTargets = (targets) =>
  targets?.map((target) => ({
    ...target,
    component: resolveContentComponent(target, mappings)?.componentType,
  }))
const getForcedVariantId = (text: string): string => (text.match(/vfcanvas-optimizely_variant_(\d+)/) || [])[1] || ''
const getVariantKey = (key: string): string => (key.match(/\d+-(.+)/) || [])[1] || ''
const getVariantId = (text: string): string => (text.match(/^(\d+)-/) || [])[1] || ''
const experiment = ref<OptimizelyDecision | null | undefined>(null)
const isSkeleton = ref(enableOptimizely)

const onDecision = ({ type, decisionInfo }) => {
  if (type === 'flag') {
    $gtm.push('optimizely.onOptimizelydecision', decisionInfo)
  }
}

$optimizelyClient?.notificationCenter.addNotificationListener(
  enums.NOTIFICATION_TYPES.DECISION,
  onDecision as any
)

const forcedVariants = route.query.p13n_testcontext ? JSON.parse(route.query.p13n_testcontext as string)?.variants : []
const flagKey = content.experienceId?.match(/optimizely_experience_(.+)$/)?.[1] || ''
const forcedVariantsIds = forcedVariants.map((variant) => getForcedVariantId(variant))
experiment.value = enableOptimizely ? decide(flagKey) : null
const cmsVariants = computed(() => {
  return forcedVariantsIds?.length
    ? content.variants.find(
      ({ variantId }) => forcedVariantsIds.includes(getVariantId(variantId))
    )?.targets
    : content.variants.find(
      ({ variantId }) => getVariantKey(variantId) === experiment.value?.variationKey
    )?.targets
})

const cmsVariantToLoad = computed(() => {
  return mapTargets(cmsVariants.value?.length ? cmsVariants.value : content.baselines)
})
watch(
  () => route.fullPath,
  () => {
    if (enableOptimizely && flagKey) {
      experiment.value = decide(flagKey)
      log.info(`Optimizely variation chosen for component: ${content.id} was ${experiment.value?.variationKey}`)
    }
  }
)

onMounted(() => {
  experiment.value = enableOptimizely ? decide(flagKey) : null
  requestAnimationFrame(() => {
    isSkeleton.value = false
  })
})

onUnmounted(() => {
  $optimizelyClient?.notificationCenter.clearAllNotificationListeners()
})
</script>
